import { Roles } from '@common/types/user';
import { CompanyManagement } from '@company/components/layout/CompanyManagement';
import { CompanyManagementContext } from '@company/contexts/CompanyManagementContext';
import { useGetManagementUserCompaniesQuery } from '@company/services/company';
import { ICompany } from '@company/types/company';
import { LoadingContainer } from '@components/display/loading/LoadingContainer';
import { useAppDispatch, useAppSelector } from '@hooks/generic';
import { setTitle } from '@store/appSlice';
import { selectUser } from '@store/userSlice';
import { useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { NotFound } from 'src/routes/NotFound';

const DashboardCompanyOverview = () => {
    const dispatch = useAppDispatch();
    const [searchParams] = useSearchParams();
    const user = useAppSelector(selectUser);
    const { data: managementUserCompaniesResponse, isLoading: isUserCompaniesLoading } =
        useGetManagementUserCompaniesQuery({});
    const managementUserCompanies = managementUserCompaniesResponse as ICompany[] | undefined;

    const selectedCompany = useMemo(
        () =>
            searchParams.get('id') && !isUserCompaniesLoading
                ? managementUserCompanies?.find((company) => company.id === searchParams.get('id'))
                : managementUserCompanies?.[0],
        [isUserCompaniesLoading, searchParams, managementUserCompanies]
    );

    const isLoading = useMemo(() => isUserCompaniesLoading, [isUserCompaniesLoading]);
    const permissions = useMemo(
        () => ({
            filter: user ? [Roles.VICTRON, Roles.SALES_DIRECTOR, Roles.SALES_MANAGER].includes(user.role) : false,
            remove: user ? user.role === Roles.VICTRON : false,
            edit: user ? [Roles.VICTRON, Roles.SALES_DIRECTOR, Roles.SALES_MANAGER].includes(user.role) : false,
            partnerships: {
                view: user ? [Roles.VICTRON, Roles.SALES_DIRECTOR, Roles.SALES_MANAGER].includes(user.role) : false,
                manage: user ? user.role === Roles.VICTRON : false,
            },
        }),
        [user]
    );

    useEffect(() => {
        dispatch(setTitle('Company Overview'));
    }, [dispatch]);

    return (
        <>
            {selectedCompany && searchParams.get('id') ? (
                <title>Company Dashboard: {selectedCompany?.name}</title>
            ) : null}
            <div className="font-inter h-full">
                <CompanyManagementContext.Provider value={{ permissions, company: selectedCompany }}>
                    <LoadingContainer isLoading={isLoading}>
                        {managementUserCompanies && managementUserCompanies.length > 0 && selectedCompany ? (
                            <CompanyManagement />
                        ) : Number(managementUserCompanies?.length) <= 0 || !managementUserCompanies ? (
                            <div>No companies available</div>
                        ) : (
                            <NotFound />
                        )}
                    </LoadingContainer>
                </CompanyManagementContext.Provider>
            </div>
        </>
    );
};

export { DashboardCompanyOverview };
